import { useState } from 'react'

import { useClearJobHistory } from '@/api/hooks'
import { Button, Dialog, useToast } from '@/components/ui'

interface ClearHistoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Finished entries currently in the list. */
  historyCount: number
  failedCount: number
  activeCount: number
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`
}

export function ClearHistoryDialog({
  open,
  onOpenChange,
  historyCount,
  failedCount,
  activeCount,
}: ClearHistoryDialogProps) {
  const clear = useClearJobHistory()
  const toast = useToast()
  const [pendingScope, setPendingScope] = useState<'all' | 'failed' | null>(null)

  const run = (scope: 'all' | 'failed') => {
    setPendingScope(scope)
    clear.mutate(scope === 'failed' ? 'failed' : undefined, {
      onSuccess: (result) => {
        onOpenChange(false)
        toast.success(
          result.deleted === 0
            ? 'Nothing to clear'
            : `Cleared ${plural(result.deleted, 'entry', 'entries')}`,
          'Your downloaded music is untouched.',
        )
      },
      onError: () => toast.error('Could not clear the history', 'Try again in a moment.'),
      onSettled: () => setPendingScope(null),
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Clear download history?"
      description={`This removes ${plural(historyCount, 'finished entry', 'finished entries')} from this list.`}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={clear.isPending}>
            Cancel
          </Button>
          {failedCount > 0 && failedCount < historyCount ? (
            <Button
              variant="secondary"
              loading={clear.isPending && pendingScope === 'failed'}
              disabled={clear.isPending}
              onClick={() => run('failed')}
            >
              Clear {plural(failedCount, 'failure', 'failures')}
            </Button>
          ) : null}
          <Button
            variant="danger"
            loading={clear.isPending && pendingScope === 'all'}
            disabled={clear.isPending}
            onClick={() => run('all')}
          >
            Clear everything
          </Button>
        </div>
      }
    >
      <p className="text-sm text-text-muted">
        Your downloaded music stays in your library — this only clears the record of what was
        fetched and when.
      </p>
      {activeCount > 0 ? (
        <p className="mt-2 text-sm text-text-muted">
          {plural(activeCount, 'download', 'downloads')} still in progress will keep going.
        </p>
      ) : null}
    </Dialog>
  )
}
